<script setup lang="ts">
  import ContactsCheckbox from '@/shared/components/rebranding/ContactsCheckbox.vue'
  import { FlowCard } from '@/shared/components/rebranding/FlowCard'
  import RemoveWindow from '@/shared/components/rebranding/RemoveWindow'
  import { useApiNew } from '@/shared/composables/useApi'
  import { useGoogleAnalytics } from '@/shared/composables/useGoogleAnalytics/useGoogleAnalytics'
  import { useScrollToTop } from '@/shared/composables/useScroll'
  import { useValidators } from '@/shared/composables/useValidators'
  import { MAX_DATE } from '@/shared/constants/dates.constants'
  import {
    analyticsPageMapping,
    AUTO_COMP_CODE,
    AUTO_COMP_KEYS,
    GENDERS,
    NAME_MAX_LENGTH,
  } from '@/shared/constants/other.constants'
  import { useCoreStore, useVisitorsStore } from '@/shared/store'
  import { PurchaseFlowRoutes } from '@/shared/types/router.types'
  import { PhoneInfo, VisitorType } from '@/shared/types/store.types'
  import { setFormatDate } from '@/shared/utils'
  import {
    AlertQC,
    ButtonQC,
    CountrySelectQC,
    InputDateQC,
    InputEmailQC,
    InputQC,
    LoaderQC,
    RadioQC,
    SelectQC,
    ToggleQC,
    WrapperQC,
    FileUploaderQC,
    ModalQC,
  } from '@qicgroupdoha/qic-connect-ds'
  import { storeToRefs } from 'pinia'
  import { computed, defineAsyncComponent, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { useRoute } from 'vue-router'
  import VisitorsPhoneInput from '@/shared/components/rebranding/VisitorsPhoneInput/VisitorsPhoneInput.vue'
  import { CountryWithDetailsType, EmailDomain } from '@/shared/types/other.types'
  import {
    LETTERS_AND_SPACES_MASK_CONFIG,
    PASSPORT_MASK_CONFIG,
    BIRTH_MIN_DATE,
    BIRTH_DATE_DEFAULT_VALUE,
  } from './VisitorForm.constants'
  import { useOCR } from '@/shared/composables/useOCR/useOCR'
  import BlockTitle from './components/BlockTitle/BlockTitle.vue'
  import { useOrder } from './VisitorForm.composables'
  import AccordionButton from './components/AccordionButton/AccordionButton.vue'
  import { useValidation } from '@qicgroupdoha/qic-use'
  import { useFindCountries } from '@/shared/composables/useFindCountries'
  import { useFeaturesStore } from '@/shared/store/feature'

  interface Props {
    visitor: VisitorType
    isHolder?: boolean
    isEdit?: boolean
    isLoading?: boolean
    emailIsValid?: boolean
  }

  interface Emits {
    (e: 'save', value: VisitorType): void
    (e: 'setEmailIsValid'): void
    (e: 'delete', value: VisitorType): void
    (e: 'openRemoveWindowEvent', value: VisitorType): void
    (e: 'closeRemoveWindowEvent', value: VisitorType): void
  }

  const props = withDefaults(defineProps<Props>(), {
    isHolder: false,
    isEdit: false,
    isLoading: false,
    emailIsValid: false,
  })
  const emit = defineEmits<Emits>()
  const VisitorsReminder = defineAsyncComponent(
    () => import('@/shared/components/rebranding/atoms/VReminder.vue')
  )

  const visitorsStore = useVisitorsStore()
  const coreStore = useCoreStore()
  const featuresStore = useFeaturesStore()
  const route = useRoute()
  const { t, locale } = useI18n()
  const gaService = useGoogleAnalytics()
  const { findCountries } = useFindCountries()
  const {
    relationshipValidator,
    required,
    nationalityValidator,
    countryValidator,
    emailValidator,
    withMessage,
    passportValidator,
  } = useValidators()

  const passportMinDate = ref(new Date(setFormatDate(new Date(), 'en')))
  const privacyPolicyLink = ref('')
  const showRemoveWindow = ref(false)
  const visitorsPhoneInput = ref<InstanceType<typeof VisitorsPhoneInput>>()
  const emailDomains = ref<EmailDomain[]>([])
  const filtredEmailDomains = ref<EmailDomain[]>([])
  const emailDomainsLoading = ref(false)
  const phoneIsCompleted = ref(false)
  const selectedNationality = ref()
  const previousSelectedNationality = ref()
  const isNationalityChangeModalVisible = ref(false)
  const isRemoveOcrModalVisible = ref(false)
  const isNationalityChangedOnClose = ref(false)
  const ocrRemoveResolver = ref<((value: boolean) => void) | null>(null)
  const emailError = computed(() => (errors.email?.length > 0 ? [errors.email[0] as string] : []))

  const isMaritalStatusEnabled = computed(() => featuresStore.isMaritalStatusEnabled)

  const nationalityIso = computed(
    () =>
      localCountries.value.find((country) => country.code === person.value.nationality)?.iso ?? ''
  )
  const ocr = useOCR(nationalityIso)
  const isFileUploaderShown = computed(
    () => ['in', 'pk'].includes(nationalityIso.value) && ocr.isOcrAvailable
  )
  const isOcrManualMode = ref(false)
  const person = ref<VisitorType>({
    ...props.visitor,
    fullName: `${props.visitor.firstName} ${props.visitor.lastName}`.trim(),
  })
  const isDisabledProperties = props.isEdit && !!person.value.visaValidityDate
  const { getOrder } = useOrder(ocr.isOcrAvailable, isDisabledProperties)
  const trackPrivacyPolicy = () => {
    let page
    if (props.isEdit) {
      page = props.isHolder ? 'update_holder' : 'update_traveller'
    } else {
      page = props.isHolder ? 'add_holder' : 'add_traveller'
    }

    gaService.sendClickPrivacyPolicy(page)
  }

  const isNotOcrManualMode = computed(() => !isOcrManualMode.value && isFileUploaderShown.value)

  const ocrModalTexts = computed(() => {
    if (isNationalityChangeModalVisible.value) {
      return {
        title: t(
          'visitors.fill_visitors_info.file_recognition.change_nationality_modal.header.text'
        ),
        subTitle: t(
          'visitors.fill_visitors_info.file_recognition.change_nationality_modal.sub_header.text'
        ),
        keepButton: t(
          'visitors.fill_visitors_info.file_recognition.change_nationality_modal.keep.button'
        ),
        removeButton: t(
          'visitors.fill_visitors_info.file_recognition.change_nationality_modal.remove.button'
        ),
      }
    }
    return {
      title: t('visitors.fill_visitors_info.file_recognition.clear_dnd_modal.header.text'),
      subTitle: t('visitors.fill_visitors_info.file_recognition.clear_dnd_modal.sub_header.text'),
      keepButton: t('visitors.fill_visitors_info.file_recognition.clear_dnd_modal.keep.button'),
      removeButton: t('visitors.fill_visitors_info.file_recognition.clear_dnd_modal.remove.button'),
    }
  })

  const checkOcrNationality = (nationality: Nullable<string>) =>
    ocr.isOcrAvailable &&
    props.isHolder &&
    visitorsStore.lastHolderNationality &&
    nationality &&
    nationality !== visitorsStore.lastHolderNationality

  const checkOcrManualMode = () => {
    if (
      isNotOcrManualMode.value &&
      person.value.firstName &&
      person.value.lastName &&
      person.value.birthDate &&
      person.value.passport &&
      person.value.country &&
      person.value.nationality
    ) {
      toggleOcrManualMode()
    }
  }

  const localFindNationality = (searchString: string) =>
    findCountries(searchString, localNationalities.value)
  const localFindCountry = (searchString: string) =>
    findCountries(searchString, localCountries.value)

  const openNationalityChangeModal = () => {
    isNationalityChangeModalVisible.value = true
  }
  const openRemoveOcrModal = (): Promise<boolean> => {
    gaService.sendClickUploader('click_icon', analyticsPages.value, 'clear')
    isRemoveOcrModalVisible.value = true
    return new Promise((resolve) => {
      ocrRemoveResolver.value = resolve
    })
  }

  const sendCloseOcrModal = (
    isClickBtn: boolean,
    isChangeNationality: boolean,
    btnTitle?: string
  ) => {
    gaService.sendCloseOcrModal(
      isClickBtn ? 'click_btn' : 'close_popup',
      analyticsPages.value,
      isChangeNationality ? 'change_nationality' : 'want_to_remove_file',
      btnTitle
    )
  }

  const closeNationalityChangeModal = (closeCallback?: () => void, keepNationality?: boolean) => {
    if (keepNationality) {
      selectedNationality.value = previousSelectedNationality.value
      person.value.nationality = selectedNationality.value.code
      isNationalityChangedOnClose.value = true
      sendCloseOcrModal(!!closeCallback, true, closeCallback ? 'keep' : undefined)
    } else {
      ocr.cancelRequest(resetOcrData)
      ocr.uploadResult.value = undefined
      person.value.nationality = selectedNationality.value.code
      sendCloseOcrModal(true, true, 'change')
    }
    isNationalityChangeModalVisible.value = false
    closeCallback?.()
  }

  const closeRemoveOcrModal = (closeCallback?: () => void, keepOcr?: boolean) => {
    if (keepOcr) {
      ocrRemoveResolver.value?.(false)
      sendCloseOcrModal(!!closeCallback, false, closeCallback ? 'keep' : undefined)
    } else {
      ocr.uploadResult.value = undefined
      ocrRemoveResolver.value?.(true)
      sendCloseOcrModal(true, false, 'remove')
    }
    ocrRemoveResolver.value = null
    isRemoveOcrModalVisible.value = false
    closeCallback?.()
  }

  const personValidation = computed(() => {
    const commonValidation = {
      firstName: { required },
      nationality: { nationalityValidator },
      country: { countryValidator },
      birthDate: { required },
      passportExpiryDate: {
        required,
        checkMinDate: withMessage(
          t('visitors.fill_visitors_info.file_recognition.passport_expiry_date'),
          (value: Nullable<Date | string>) =>
            value ? passportMinDate.value <= new Date(value) : true
        ),
      },
      passport: { ...passportValidator },
      email: {
        ...emailValidator,
        checkEmail: withMessage(t('qic_error_invalid_input'), () => props.emailIsValid),
      },
    }
    const additionalValidation: Record<string, unknown> = {}
    if (isDisabledProperties) {
      additionalValidation.fullName = { required }
    } else {
      additionalValidation.lastName = { required }
    }
    if (!props.isHolder) {
      additionalValidation.relationship = { relationshipValidator }
    }

    if (isNotOcrManualMode.value) {
      additionalValidation.uploadResult = {
        checkUploadResult: withMessage(
          t('visitors.fill_visitors_info.file_recognition.no_file_validation_error'),
          () => ocr.uploadResult.value !== 'error'
        ),
      }
    }

    if (isMaritalStatusEnabled.value) {
      additionalValidation.maritalStatus = { required }
    }
    return {
      ...commonValidation,
      ...additionalValidation,
    }
  })

  const { nationalities, countries, relations, maritalStatuses } = storeToRefs(coreStore)
  const localNationalities = computed(() =>
    nationalities.value?.map((nat) => ({
      name: nat.desc,
      iso: nat.flag,
      ...nat,
    }))
  )
  const localCountries = computed((): CountryWithDetailsType[] =>
    countries.value?.map<CountryWithDetailsType>((country) => ({
      name: country.desc,
      iso: country.flag,
      ...country,
    }))
  )

  const isHolderInfoPage = computed(() => route.name === PurchaseFlowRoutes.HOLDER_INFO)

  const analyticsPages = computed(() => {
    if (isHolderInfoPage.value) {
      return analyticsPageMapping['holder-info']
    }
    return props.isEdit ? analyticsPageMapping['edit-visitor'] : analyticsPageMapping['add-visitor']
  })

  const personValidationData = computed(() => {
    const data: Record<string, unknown> = {
      ...person.value,
    }
    if (isNotOcrManualMode.value) {
      data.uploadResult = ocr.uploadResult.value
    }
    return data
  })
  const { v$, errors } = useValidation(personValidation, personValidationData)

  const btnText = computed(() => {
    if (isHolderInfoPage.value) {
      return ocr.isOcrAvailable ? t('quote_proceed') : t('qlm_proceed')
    }

    return person.value.srNo > 0 ? t('qlm_update') : t('qlm_add_visitor')
  })

  const btnSymbol = computed(() => {
    if (!isHolderInfoPage.value) return undefined
    return locale.value === 'en' ? 'arrow-narrow-right' : 'arrow-narrow-left'
  })
  const headerTitle = computed(() => {
    if (!props.isHolder && ocr.isOcrAvailable) return undefined
    if (ocr.isOcrAvailable)
      return props.isEdit
        ? t('qlm_personal_info_header_edit')
        : t('visitors.fill_visitors_info.personal_info_header')
    return props.isHolder ? t('qlm_personal_info_header') : t('personal_info.additional.header')
  })

  const setSelectedCountry = (code: Nullable<string>) => {
    selectedCountry.value = localCountries.value.find((country) => country.code === code)
  }

  const setSelectedNationality = (code: Nullable<string>) => {
    selectedNationality.value = localNationalities.value.find(
      (nationality) => nationality.code === code
    )
  }

  const nextFocus = (event: Event) => {
    if ((event as KeyboardEvent).code === 'Enter') {
      const curTabindex = (event.target as HTMLInputElement)?.tabIndex
      const nextTabindex = document.querySelector(`[tabindex='${curTabindex + 1}']`) as HTMLElement
      if (nextTabindex) {
        nextTabindex.focus()
      }
      ;(event.target as HTMLInputElement)?.blur()
    }
  }

  const toggleOcrManualMode = () => {
    isOcrManualMode.value = !isOcrManualMode.value
    v$.value.$reset()
    if (isOcrManualMode.value) {
      ocr.cancelRequest()
      // TODO: this is hack, resolve in the qic-connect-ds
      ocr.uploadResult.value = undefined
    }
  }

  const submit = () => {
    if (ocr.isProcessing.value) return

    if (!ocr.uploadResult.value && isNotOcrManualMode.value) {
      ocr.uploadResult.value = 'error'
    }

    v$.value.$touch()
    if (v$.value.$error) return

    const relationship = relations.value.find((item) => item.code === person.value.relationship)
    const nationality = nationalities.value.find((item) => item.code === person.value.nationality)

    if (relationship) {
      person.value.relationshipName = relationship.desc
    }

    if (nationality) {
      person.value.nationalityName = nationality.desc
    }
    emit('save', person.value)
  }

  const updateContactsSame = (newValue: boolean) => {
    if (props.isHolder) return
    const { email } = visitorsStore.holder

    if (newValue) {
      person.value.email = email
    } else {
      person.value.email = props.visitor.email !== email ? props.visitor.email : email
    }
  }

  const switchRemoveWindow = (action: 'open' | 'close', person: VisitorType) => {
    showRemoveWindow.value = !showRemoveWindow.value
    return action === 'open'
      ? emit('openRemoveWindowEvent', person)
      : emit('closeRemoveWindowEvent', person)
  }

  const onInputEmail = (value: string | number | null) => {
    emit('setEmailIsValid')

    if (!emailDomains.value.length) return

    const domainPart = value?.toString().split('@')?.[1] || ''
    if (!domainPart.length) {
      filtredEmailDomains.value = emailDomains.value.filter((domain) => domain.level === 2)
      return
    }

    filtredEmailDomains.value = emailDomains.value
  }

  const setPhoneInfo = (phoneInfo: PhoneInfo) => {
    person.value.phoneInfo = { ...phoneInfo }
  }

  const handleSelectedDomain = (domain: string) => {
    gaService.sendEmailAutoFill(domain, analyticsPages.value)
  }

  onMounted(async () => {
    const forms = document.querySelectorAll('input')
    forms.forEach((el) => {
      el.addEventListener('keydown', nextFocus)
    })
    setTimeout(useScrollToTop, 0)
    updateContactsSame(person.value.contactsSame)
    coreStore.getDurations()
    privacyPolicyLink.value = await useApiNew().commonSeervice.getPrivacyPolicyLink()
    if (AUTO_COMP_KEYS.includes(coreStore.getAbValue(AUTO_COMP_CODE))) {
      emailDomainsLoading.value = true
      emailDomains.value = await coreStore.getEmailDomains()
      emailDomainsLoading.value = false
    }

    if (props.isEdit) {
      toggleOcrManualMode()
    }
    nextTick(checkOcrManualMode)
  })

  onUnmounted(() => {
    const forms = document.querySelectorAll('input')
    forms.forEach((el) => {
      el.removeEventListener('keydown', nextFocus)
    })
  })

  watch(
    selectedNationality,
    (newValue, oldValue) => {
      if (ocr.isProcessing.value || ocr.uploadResult.value === 'success') {
        if (props.isHolder) return
        if (isNationalityChangedOnClose.value) {
          isNationalityChangedOnClose.value = false
          return
        }
        if (oldValue.code !== newValue.code) {
          previousSelectedNationality.value = oldValue
          openNationalityChangeModal()
        }
      }
      if (!isNationalityChangeModalVisible.value) {
        person.value.nationality = selectedNationality.value.code
      }
      v$.value.nationality.$reset()
    },
    {
      flush: 'post',
    }
  )

  watch(
    localNationalities,
    () => {
      setSelectedNationality(person.value.nationality)
    },
    {
      immediate: true,
    }
  )

  const selectedCountry = ref<CountryWithDetailsType>()
  watch(
    () => selectedCountry.value,
    () => {
      person.value.country = selectedCountry.value?.code || null
      v$.value.country.$reset()
      if (!phoneIsCompleted.value && selectedCountry.value?.iso) {
        visitorsPhoneInput.value?.setCountryCode(selectedCountry.value.iso.toUpperCase())
      }
    }
  )

  watch(
    localCountries,
    () => {
      setSelectedCountry(person.value.country)
    },
    {
      immediate: true,
    }
  )

  watch(
    () => person.value.contactsSame,
    (isContactsSame) => {
      if (props.isHolder) return
      updateContactsSame(isContactsSame)
    },
    {
      immediate: true,
    }
  )

  watch(
    () => props.visitor,
    (newValue) => {
      if (!newValue || !newValue.firstName?.length) return
      person.value = { ...newValue }
      person.value.fullName = `${newValue.firstName} ${newValue.lastName}`.trim()
      if (checkOcrNationality(newValue.nationality) && visitorsStore.lastHolderNationality) {
        setSelectedNationality(visitorsStore.lastHolderNationality)
        person.value.nationality = visitorsStore.lastHolderNationality
      } else {
        setSelectedNationality(newValue.nationality)
      }
      setSelectedCountry(newValue.country)
      person.value.passportExpiryDate = person.value.passportExpiryDate
        ? new Date(person.value.passportExpiryDate)
        : null
      if (
        person.value.passportExpiryDate !== null &&
        person.value.passportExpiryDate < passportMinDate.value
      ) {
        person.value.passportExpiryDate = null
      }
      checkOcrManualMode()
    },
    {
      flush: 'post',
      once: true,
    }
  )

  const resetOcrData = () => {
    v$.value.$reset()
    person.value.lastName = ''
    person.value.firstName = ''
    person.value.passport = ''
    person.value.birthDate = null
    person.value.passportExpiryDate = null
    person.value.gender = GENDERS.MALE
  }

  const handleProcessFile = async (file: File) => {
    resetOcrData()

    const res = await ocr.processFile(file)
    if (!res) return

    person.value.lastName = res.familyName
    person.value.firstName = res.firstName
    person.value.passport = res.passportNumber
    person.value.gender = res.gender === 'male' ? GENDERS.MALE : GENDERS.FEMALE
    person.value.birthDate = res.dateOfBirth
    person.value.passportExpiryDate = res.dateOfExpiry

    await nextTick()
    v$.value.passportExpiryDate.$touch()
  }

  // TODO: because component double click on uploader, we need to send analytics only once
  let uploaderClickSent = false
  const clickUploader = () => {
    if (uploaderClickSent) return
    uploaderClickSent = true
    gaService.sendClickUploader('click_upload_doc', analyticsPages.value)
    setTimeout(() => {
      uploaderClickSent = false
    }, 100)
  }
</script>

<template>
  <WrapperQC direction="column" gap="var(--spacing-8)">
    <FlowCard
      v-if="!isHolder && ocr.isOcrAvailable"
      class="visitor-form"
      :class="{
        'visitor-form--loading': props.isLoading,
      }"
      :title="t('visitors.fill_visitors_info.personal_info.additional.header')"
      :with-button="false"
      padding="var(--spacing-8)"
    >
      <template #subtitle>
        <!-- @vue-ignore -->
        <p
          class="visitor-form__annotation"
          :class="{
            'text-medium': !$qicConnect.isMobile,
            'text-regular': $qicConnect.isMobile,
          }"
        >
          {{ $t('qic_personal_info_annotation') }}
        </p>
      </template>
      <WrapperQC direction="column" gap="var(--spacing-5)">
        <!-- @vue-ignore -->
        <SelectQC
          v-model="person.relationship"
          :options="relations"
          :label="$t('qlm_relationship')"
          :is-valid="!v$.relationship.$dirty && !v$.relationship.$invalid"
          :errors="errors.relationship"
          title-key="desc"
          value-key="code"
          name="relationship"
          is-filterable
          data-test="relationship-select"
          :locale="locale"
        />
        <CountrySelectQC
          v-model="selectedNationality"
          with-input
          :label="
            ocr.isOcrAvailable
              ? $t('visitors.travel_dates_page.nationality')
              : $t('qlm_nationality')
          "
          :select-props="{
            errors: errors.nationality,
            disabled: isDisabledProperties,
            isValid: !v$.nationality.$dirty && !v$.nationality.$invalid,
            hideArrowIcon: !v$.nationality.$dirty && !v$.nationality.$invalid,
            name: 'nationality',
            filterFunction: localFindNationality,
            locale,
          }"
          :countries="localNationalities"
          is-filterable
          data-test="nationality-select"
        />
      </WrapperQC>
    </FlowCard>

    <FlowCard
      class="visitor-form"
      :class="{
        'visitor-form--loading': props.isLoading,
      }"
      :title="headerTitle"
      :with-button="false"
      :padding="ocr.isOcrAvailable ? 'var(--spacing-8)' : 'var(--spacing-9)'"
    >
      <template v-if="(!isEdit && isHolder) || (!isHolder && !ocr.isOcrAvailable)" #subtitle>
        <!-- @vue-ignore -->
        <p
          v-if="!isEdit && isHolder"
          class="visitor-form__main-annotation"
          :class="{
            'text-medium': !$qicConnect.isMobile,
            'text-regular': $qicConnect.isMobile,
          }"
        >
          {{ $t('travel_dates.policy_holder.annotation_1') }}
          {{ $t('travel_dates.policy_holder.annotation_2') }}
          {{ $t('travel_dates.policy_holder.annotation_3') }}
        </p>
        <!-- @vue-ignore -->
        <p
          v-else-if="!isHolder && !ocr.isOcrAvailable"
          class="visitor-form__annotation"
          :class="{
            'text-medium': !$qicConnect.isMobile,
            'text-regular': $qicConnect.isMobile,
          }"
        >
          {{ $t('qic_personal_info_annotation') }}
        </p>
      </template>

      <AlertQC
        v-if="!ocr.isOcrAvailable && isDisabledProperties"
        variant="success"
        :subtitle="$t('qic_personal_info_additional_annotation')"
        with-icon
      />

      <WrapperQC
        v-if="isFileUploaderShown && !isOcrManualMode"
        direction="column"
        gap="var(--spacing-7)"
        class="visitor-form__uploader"
      >
        <BlockTitle
          v-if="!isOcrManualMode"
          icon="passport"
          :text="$t('visitors.fill_visitors_info.upload_passport.heading.text')"
        />
        <KeepAlive>
          <FileUploaderQC
            v-if="!isOcrManualMode"
            v-model:upload-result="ocr.uploadResult.value"
            :locale="locale"
            size="medium"
            auto-progress
            :error="(errors.uploadResult?.[0] as string) ?? ocr.errorMessage.value"
            :max-file-size="20"
            :i18n="{
              hint: {
                en: $t('visitors.fill_visitors_info.file_recognition.type_size_text'),
                ar: $t('visitors.fill_visitors_info.file_recognition.type_size_text'),
              },
            }"
            :before-cancel="openRemoveOcrModal"
            data-test="file-uploader"
            @file-selected="handleProcessFile"
            @cancel="ocr.cancelRequest(resetOcrData)"
            @error:file-size="ocr.setErrorFileSize"
            @click="clickUploader"
          />
        </KeepAlive>
      </WrapperQC>

      <WrapperQC
        v-if="ocr.isOcrAvailable && isFileUploaderShown"
        direction="column"
        gap="var(--spacing-7)"
        :style="{ order: ocr.isProcessing.value || ocr.uploadResult.value === 'success' ? 2 : 1 }"
        class="visitor-form__uploader-divider"
      >
        <AccordionButton v-if="isOcrManualMode" icon="passport" @click="toggleOcrManualMode">
          {{ $t('visitors.fill_visitors_info.change_mode_ocr.button') }}
        </AccordionButton>

        <div class="visitor-form__divider text-medium">
          {{ $t('visitors.fill_visitors_info.or.text') }}
        </div>
        <AccordionButton v-if="!isOcrManualMode" icon="hand" @click="toggleOcrManualMode">
          {{ $t('visitors.fill_visitors_info.change_mode_manually.button') }}
        </AccordionButton>
      </WrapperQC>

      <div
        v-if="
          (isFileUploaderShown && (isOcrManualMode || ocr.isProcessing.value)) ||
          !isFileUploaderShown ||
          ocr.uploadResult.value === 'success'
        "
        class="visitor-form__inputs"
        :style="{
          order:
            ocr.isProcessing.value || (ocr.uploadResult.value === 'success' && !isOcrManualMode)
              ? 1
              : 2,
        }"
      >
        <BlockTitle
          v-if="!isFileUploaderShown || (ocr.isOcrAvailable && isOcrManualMode)"
          icon="passport"
          :text="$t('visitors.fill_visitors_info.passport_details_block.header')"
        />
        <AlertQC
          v-if="ocr.isOcrAvailable && isDisabledProperties"
          variant="success"
          :subtitle="$t('qic_personal_info_additional_annotation')"
          with-icon
          style="margin-block: var(--spacing-3)"
        />
        <!-- @vue-ignore -->
        <SelectQC
          v-if="!isHolder && !ocr.isOcrAvailable"
          v-model="person.relationship"
          :options="relations"
          :label="$t('qlm_relationship')"
          :is-valid="!v$.relationship.$dirty && !v$.relationship.$invalid"
          :errors="errors.relationship"
          title-key="desc"
          value-key="code"
          name="relationship"
          is-filterable
          data-test="relationship-select"
          :locale="locale"
        />
        <template v-if="!isDisabledProperties">
          <InputQC
            v-model="person.firstName"
            :errors="(errors.firstName as string[])"
            name="firstName"
            :label="$t('qic_user_name_first')"
            :max-length="NAME_MAX_LENGTH"
            :is-valid="!v$.firstName.$invalid"
            :mask-config="LETTERS_AND_SPACES_MASK_CONFIG"
            :container-attrs="{
              'data-test': 'first-name-input',
              style: {
                order: getOrder('firstName'),
              },
            }"
            :loading="ocr.isProcessing.value"
            data-test="first-name-input-inner"
            @blur="person.firstName = person.firstName.trim()"
          />

          <InputQC
            v-model="person.lastName"
            :errors="(errors.lastName as string[])"
            name="lastName"
            :label="$t('qic_user_name_family')"
            :max-length="NAME_MAX_LENGTH"
            :is-valid="!v$.lastName.$invalid"
            :mask-config="LETTERS_AND_SPACES_MASK_CONFIG"
            :container-attrs="{
              'data-test': 'last-name-input',
              style: {
                order: getOrder('lastName'),
              },
            }"
            :loading="ocr.isProcessing.value"
            data-test="last-name-input-inner"
            @blur="person.lastName = person.lastName.trim()"
          />
        </template>

        <InputQC
          v-else
          v-model="person.fullName"
          name="fullName"
          :label="$t('full_name_placeholder')"
          is-valid
          disabled
          :container-attrs="{
            'data-test': 'full-name-input',
            style: {
              order: getOrder('fullName'),
            },
          }"
          data-test="full-name-input-inner"
        />

        <div
          :style="{ order: getOrder('birthDate') }"
          style="display: flex; flex-direction: column"
        >
          <!-- @vue-ignore -->
          <InputDateQC
            v-model="person.birthDate"
            :label="$t('qic_user_dob')"
            :min-date="BIRTH_MIN_DATE"
            :max-date="MAX_DATE"
            :errors="errors.birthDate"
            :locale="locale"
            :disabled="isDisabledProperties"
            :calendar-props="{
              startDate: person.birthDate ?? BIRTH_DATE_DEFAULT_VALUE,
            }"
            :input-props="{
              isValid: !v$.birthDate.$invalid,
              disabled: isDisabledProperties,
              containerAttrs: {
                'data-test': 'birth-date-input',
              },
              name: 'birthDate',
              loading: ocr.isProcessing.value,
            }"
            :hide-calendar-icon="!v$.birthDate.$invalid"
            allow-manual-input
            data-test="birth-date-input-inner"
          />
        </div>

        <div
          class="radio-container"
          :class="{ 'radio-container--paddings': !ocr.isOcrAvailable }"
          :style="{ order: getOrder('gender') }"
        >
          <!-- @vue-ignore -->
          <RadioQC
            v-if="!isDisabledProperties || person.gender === GENDERS.MALE"
            v-model="person.gender"
            :value="GENDERS.MALE"
            :disabled="isDisabledProperties"
            :container-attrs="{
              'data-test': 'gender-male-radio',
            }"
            :variant="ocr.isOcrAvailable ? 'mini' : 'default'"
            :loading="ocr.isProcessing.value"
            data-test="gender-male-radio-inner"
          >
            {{ $t('qic_user_male') }}
          </RadioQC>

          <!-- @vue-ignore -->
          <RadioQC
            v-if="!isDisabledProperties || person.gender === GENDERS.FEMALE"
            v-model="person.gender"
            :value="GENDERS.FEMALE"
            :disabled="isDisabledProperties"
            :container-attrs="{
              'data-test': 'gender-female-radio',
            }"
            :variant="ocr.isOcrAvailable ? 'mini' : 'default'"
            :loading="ocr.isProcessing.value"
            data-test="gender-female-radio-inner"
          >
            {{ $t('qic_user_female') }}
          </RadioQC>
        </div>

        <CountrySelectQC
          v-if="!ocr.isOcrAvailable"
          v-model="selectedNationality"
          with-input
          :label="$t('qlm_nationality')"
          :select-props="{
            errors: errors.nationality,
            disabled: isDisabledProperties,
            isValid: !v$.nationality.$dirty && !v$.nationality.$invalid,
            hideArrowIcon: !v$.nationality.$dirty && !v$.nationality.$invalid,
            name: 'nationality',
            filterFunction: localFindNationality,
            locale,
          }"
          :countries="localNationalities"
          is-filterable
          data-test="nationality-select"
          :style="{ order: getOrder('nationality') }"
        />

        <span :style="{ order: getOrder('country') }">
          <CountrySelectQC
            v-model="selectedCountry"
            with-input
            :label="$t('qlm_country')"
            :select-props="{
              errors: errors.country,
              isValid: !v$.country.$dirty && !v$.country.$invalid,
              hideArrowIcon: !v$.country.$dirty && !v$.country.$invalid,
              name: 'country',
              filterFunction: localFindCountry,
              locale,
            }"
            :countries="localCountries"
            is-filterable
            data-test="country-select"
          />
        </span>

        <AlertQC
          v-if="ocr.uploadResult.value === 'success'"
          variant="brand"
          :subtitle="$t('visitors.fill_visitors_info.check_ocr_info.text')"
          with-icon
          style="order: 9"
        />

        <InputQC
          v-model="person.passport"
          :errors="(errors.passport as string[])"
          name="passport"
          :label="$t('qlm_passport')"
          :is-valid="!v$.passport.$invalid"
          :mask-config="PASSPORT_MASK_CONFIG"
          :disabled="isDisabledProperties"
          :max-length="20"
          :container-attrs="{
            'data-test': 'passport-input',
            style: {
              order: getOrder('passport'),
            },
          }"
          :loading="ocr.isProcessing.value"
          data-test="passport-input-inner"
        />
        <WrapperQC direction="column" :style="{ order: getOrder('passportExpiryDate') }">
          <!-- @vue-ignore -->
          <InputDateQC
            v-model="person.passportExpiryDate"
            :label="$t('qlm_passport_exp')"
            :min-date="passportMinDate"
            :errors="errors.passportExpiryDate"
            :calendar-props="{
              startDate: person.passportExpiryDate ?? passportMinDate,
            }"
            :input-props="{
              isValid: !v$.passportExpiryDate.$invalid,
              containerAttrs: {
                'data-test': 'passport-expiry-date-input',
              },
              name: 'passportExpiryDate',
              loading: ocr.isProcessing.value,
            }"
            :locale="locale"
            :max-date="new Date('2070-01-01')"
            :hide-calendar-icon="!v$.passportExpiryDate.$invalid"
            allow-manual-input
            data-test="passport-expiry-date-input-inner"
          />
        </WrapperQC>

        <WrapperQC v-if="isMaritalStatusEnabled" direction="column" :style="{ order: getOrder('maritalStatus') }">
          <!-- @vue-ignore -->
          <SelectQC
            v-model="person.maritalStatus"
            :options="maritalStatuses"
            :style="{ order: getOrder('maritalStatus') }"
            :label="$t('visitors.fill_visitors_info.marital_status_placeholder')"
            :is-valid="!v$.maritalStatus.$invalid"
            :errors="errors.maritalStatus"
            :container-attrs="{
              'data-test': 'marital-status-select',
            }"
            title-key="name"
            value-key="code"
            name="maritalStatus"
            :mobile-version="false"
            data-test="marital-status-select"
            :locale="locale"
          />
        </WrapperQC>

        <WrapperQC
          v-if="isDisabledProperties"
          direction="column"
          gap="var(--spacing-5)"
          :margin="{ top: 'var(--spacing-5)' }"
          style="order: 98"
        >
          <BlockTitle icon="profile" :text="$t('personal_info.visa_information_block.header')" />

          <InputQC
            v-model="person.visa"
            name="visaPreapprovalNumber"
            :label="$t('qlm_visa_number')"
            is-valid
            :container-attrs="{
              'data-test': 'visa-input',
            }"
            disabled
            data-test="visa-input-inner"
          />

          <!-- @vue-ignore -->
          <InputDateQC
            v-model="person.visaValidityDate"
            :label="$t('visa_expiry_date')"
            disabled
            :locale="locale"
            :input-props="{
              isValid: true,
              disabled: true,
              containerAttrs: {
                'data-test': 'visa-expiry-date-input',
              },
            }"
            hide-calendar-icon
            name="visaExpireDate"
            data-test="visa-expiry-date-input-inner"
          />
        </WrapperQC>

        <WrapperQC
          v-if="!ocr.isOcrAvailable"
          direction="column"
          gap="var(--spacing-5)"
          style="order: 99"
        >
          <BlockTitle
            icon="phone"
            :text="$t('qlm_contacts')"
            style="margin-top: var(--spacing-5)"
          />

          <ContactsCheckbox v-if="!isHolder">
            <template #toggle>
              <ToggleQC v-model="person.contactsSame" size="medium" data-test="contacts-toggle" />
            </template>
          </ContactsCheckbox>

          <InputEmailQC
            v-model="person.email"
            :label="$t('qic_user_contacts_email')"
            :errors="emailError"
            :is-valid="!v$.email.$invalid"
            name="email"
            :disabled="!isHolder && person.contactsSame"
            data-test="email-input"
            :domains-list="filtredEmailDomains"
            :locale="locale"
            :loading="emailDomainsLoading"
            @input="onInputEmail"
            @selected-domain="handleSelectedDomain"
          />
          <VisitorsPhoneInput
            ref="visitorsPhoneInput"
            :visitor="person"
            :disabled="!isHolder && person.contactsSame"
            :contacts-same="person.contactsSame"
            :is-edit="isEdit"
            :is-holder="isHolder"
            @phone-completed="phoneIsCompleted = $event"
            @phone-changed="setPhoneInfo"
          />
        </WrapperQC>
      </div>

      <template v-if="!ocr.isOcrAvailable">
        <div class="visitor-form__footer">
          <ButtonQC :icon-after-content="btnSymbol" data-test="proceed-button" @click="submit">
            {{ btnText }}
          </ButtonQC>

          <template v-if="!isHolder && isEdit">
            <Transition name="fade" mode="out-in">
              <ButtonQC
                v-if="!showRemoveWindow"
                variant="outline"
                icon-before-content="delete-bin"
                size="large"
                data-test="remove-user-button"
                @click="switchRemoveWindow('open', person)"
              >
                {{ $t('personal_info.additional.delete') }}
              </ButtonQC>
              <RemoveWindow
                v-else
                @cancel="switchRemoveWindow('close', person)"
                @delete="emit('delete', person)"
              />
            </Transition>
          </template>

          <VisitorsReminder
            is-privacy-policy
            :privacy-policy-link="privacyPolicyLink"
            link-test-id="privacy-policy-link"
            @track-privacy-policy="trackPrivacyPolicy"
          />
        </div>
      </template>
    </FlowCard>

    <FlowCard
      v-if="ocr.isOcrAvailable"
      padding="var(--spacing-8)"
      :with-button="false"
      class="visitor-form"
      :class="{
        'visitor-form--loading': props.isLoading,
      }"
    >
      <WrapperQC direction="column" gap="var(--spacing-5)" class="visitor-form__contacts">
        <BlockTitle icon="phone" :text="$t('visitors.fill_visitors_info.contacts.heading.text')" />

        <ContactsCheckbox v-if="!isHolder">
          <template #toggle>
            <ToggleQC v-model="person.contactsSame" size="medium" data-test="contacts-toggle" />
          </template>
        </ContactsCheckbox>

        <InputEmailQC
          v-model="person.email"
          :label="$t('qic_user_contacts_email')"
          :errors="emailError"
          :is-valid="!v$.email.$invalid"
          name="email"
          :disabled="!isHolder && person.contactsSame"
          data-test="email-input"
          :domains-list="filtredEmailDomains"
          :locale="locale"
          :loading="emailDomainsLoading"
          @input="onInputEmail"
          @selected-domain="handleSelectedDomain"
        />
        <VisitorsPhoneInput
          ref="visitorsPhoneInput"
          :visitor="person"
          :disabled="!isHolder && person.contactsSame"
          :contacts-same="person.contactsSame"
          :is-edit="isEdit"
          :is-holder="isHolder"
          @phone-completed="phoneIsCompleted = $event"
          @phone-changed="setPhoneInfo"
        />
      </WrapperQC>
      <div class="visitor-form__footer">
        <ButtonQC :icon-after-content="btnSymbol" data-test="proceed-button" wide @click="submit">
          {{ btnText }}
        </ButtonQC>

        <template v-if="!isHolder && isEdit">
          <Transition name="fade" mode="out-in">
            <ButtonQC
              v-if="!showRemoveWindow"
              variant="outline"
              icon-before-content="delete-bin"
              size="large"
              data-test="remove-user-button"
              @click="switchRemoveWindow('open', person)"
            >
              {{ $t('personal_info.additional.delete') }}
            </ButtonQC>
            <RemoveWindow
              v-else
              @cancel="switchRemoveWindow('close', person)"
              @delete="emit('delete', person)"
            />
          </Transition>
        </template>

        <VisitorsReminder
          is-privacy-policy
          :privacy-policy-link="privacyPolicyLink"
          link-test-id="privacy-policy-link"
          @track-privacy-policy="trackPrivacyPolicy"
        />
      </div>

      <div v-if="isLoading && !ocr.isOcrAvailable" class="visitor-form__loader">
        <LoaderQC size="extraLarge" variant="primary" />
      </div>
    </FlowCard>

    <div v-if="isLoading" class="visitor-form__loader">
      <LoaderQC size="extraLarge" variant="primary" />
    </div>

    <!-- @vue-ignore -->
    <ModalQC
      v-if="isNationalityChangeModalVisible || isRemoveOcrModalVisible"
      :title="ocrModalTexts.title"
      :locale="locale"
      @close="
        isNationalityChangeModalVisible
          ? closeNationalityChangeModal(undefined, true)
          : closeRemoveOcrModal(undefined, true)
      "
    >
      <template #after-title>
        {{ ocrModalTexts.subTitle }}
      </template>
      <template #controls="{ close }">
        <WrapperQC direction="column" gap="var(--spacing-4)">
          <ButtonQC
            variant="primary"
            size="large"
            wide
            @click="
              isNationalityChangeModalVisible
                ? closeNationalityChangeModal(close, true)
                : closeRemoveOcrModal(close, true)
            "
            >{{ ocrModalTexts.keepButton }}</ButtonQC
          >
          <ButtonQC
            variant="outline"
            size="large"
            wide
            @click="
              isNationalityChangeModalVisible
                ? closeNationalityChangeModal(close, false)
                : closeRemoveOcrModal(close, false)
            "
            >{{ ocrModalTexts.removeButton }}</ButtonQC
          >
        </WrapperQC>
      </template>
    </ModalQC>
  </WrapperQC>
</template>

<style lang="scss" scoped>
  @use '@scss/utils.scss';

  .visitor-form {
    /* TODO: Temporary fix for 8.0.1 release. It should be fixed in qic-connect */
    :deep(.ds-base-input__wrapper) {
      max-height: 56px;
    }

    position: relative;

    .visitor-form__inputs {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: stretch;
      gap: var(--spacing-5);
    }

    &__divider {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: var(--spacing-8);
      position: relative;
      &::before,
      &::after {
        content: '';
        width: 100%;
        height: 1px;
        background-color: var(--colorBrdrSrfPrimary);
      }
    }

    &__annotation {
      text-align: center;

      @media (max-width: 550px) {
        font-size: 14px;
      }
    }

    &__main-annotation {
      text-align: center;
    }

    &-datepicker {
      z-index: 5;
    }

    &-select {
      &--nationalities {
        z-index: 4 !important;
      }

      &--country {
        z-index: 3 !important;
      }
    }

    .radio-container {
      display: flex;
      gap: var(--spacing-7);
    }

    .radio-container--paddings {
      padding-block: calc(var(--spacing-4) + var(--spacing-2));
    }

    .gender-header {
      font-weight: 400;
      font-size: 14px;
      line-height: 24px;

      @media (max-width: 550px) {
        font-size: 16px;
        line-height: 24px;
      }
    }

    &.visitor-form--loading {
      .visitor-form__footer,
      .visitor-form__inputs,
      .visitor-form__contacts,
      .visitor-form__uploader,
      .visitor-form__uploader-divider {
        opacity: 0.2;
      }
    }
  }

  .visitor-form__contacts-title {
    margin-top: var(--spacing-5);
    order: 2;
  }

  .visitor-form__footer {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: var(--spacing-3);
    order: 99;
  }

  .visitor-form__loader {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: inherit;
    z-index: 20;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .country-selector__country-flag {
    margin-top: 4px;
    display: flex;
    align-items: center;
    gap: 12px;

    img {
      width: 24px;
      height: 24px;
      object-fit: contain;
    }
  }

  .fade {
    &-enter-active,
    &-leave-active {
      transition: opacity 0.15s ease;
    }

    &-enter-from,
    &-leave-to {
      opacity: 0;
    }
  }
</style>
